<template>
  <component :is="tag" class="faq">
    <div class="section-inner">
      <h2 v-if="blok.title" class="title size-xl">
        {{ blok.title }}
      </h2>
      <dl v-if="blok.items?.length" class="faq-list">
        <div
          v-for="(faqItem, index) in blok.items"
          :key="faqItem._uid"
          class="faq-item"
        >
          <dt class="faq-question">
            <button
              :id="`btn-faq${index}`"
              :aria-expanded="index === faqActive"
              :aria-controls="`collapse${index}`"
              class="btn-faq size-m"
              @click="faqToggle(index, $event)"
            >
              <span>{{ faqItem.content?.faq_question }}</span>
              <SvgIcon name="func_down" class="fill" />
            </button>
          </dt>
          <dd
            :id="`collapse${index}`"
            :aria-labelledby="`btn-faq${index}`"
            :class="`faq-answer${index === faqActive ? ' answer-active' : ''}`"
          >
            <div
              v-if="
                useRenderRichText(faqItem.content?.faq_answer[0]?.text)?.length
              "
              class="text size-xs"
              v-html="useRenderRichText(faqItem.content?.faq_answer[0]?.text)"
            ></div>
            <template v-if="faqItem.content?.faq_answer[0]?.sub_items?.length">
              <FaqList :blok="faqItem.content?.faq_answer[0]?.sub_items[0]" />
            </template>
          </dd>
        </div>
      </dl>
    </div>
  </component>
</template>

<script setup>
const props = defineProps({
  blok: {
    type: Object,
    required: true,
  },
  tag: {
    type: String,
    required: false,
    default: "div",
  },
});
const { isDesktop } = useDevice();
const isScrolledPage = ref(false);
let faqActive = ref(null);

const faqToggle = (faqIndex, event) => {
  const minNavHeight = isScrolledPage.value ? document.querySelector(".min-nav")?.offsetHeight : 69;
  const fullNavHeight = document.querySelector(".global-header-inner")?.offsetHeight;
  const btnHeight = document.querySelector(`#btn-faq${faqIndex}`)?.offsetHeight;
  let margin;

  if(isDesktop) {
    margin = isScrolledPage.value ? (minNavHeight + btnHeight) : (fullNavHeight + btnHeight + minNavHeight);
  } else {
    margin = btnHeight;
  }

  faqActive.value = faqIndex === faqActive.value ? null : faqIndex;

  const faqParent = event.target.offsetParent;

  setTimeout(() => {
    if(faqActive.value != null) {
      window.scrollTo({
        top: faqParent?.querySelector(".faq-answer", ".answer-active")?.getBoundingClientRect().top + window.scrollY - margin,
        behavior: "smooth",
      });
    }
  }, 200);
};

const handleScroll = () => {
  window.scrollY > document.querySelector(".global-header-inner")?.offsetHeight ? isScrolledPage.value = true : isScrolledPage.value = false;
};

onMounted(() => {
  window.addEventListener("scroll", handleScroll);
  handleScroll();  
});

onBeforeUnmount(() => {
  window.removeEventListener("scroll", handleScroll);
});
</script>

<style lang="scss" scoped>
.faq {
  &-list {
    margin: 0 auto;
    grid-column: 2/-2;
    border-radius: $radius-l;
    overflow: hidden;
    max-width: 54rem;
  }

  &-item {
    border-bottom: solid 1px var(--solid-07);

    .btn-faq {
      display: flex;
      width: 100%;
      justify-content: space-between;
      align-items: center;
      gap: 1.25rem;
      padding: 1.5rem 2rem 1.5rem 1.25rem;
      @include pair-3;
      border: 0;
      cursor: pointer;
      text-align: left;
      font-weight: 700;
      @include for-desktop-up {
        gap: 4.5rem;
        padding: 2.5rem 4.5rem 2.5rem 4.5rem;
      }

      .icon {
        width: 2rem;
        height: 2rem;
        position: absolute;
        right: 1.5rem;
        transition: all ease 0.2s;
        @include for-desktop-up {
          right: 4.5rem;  
        }
      }

      &[aria-expanded="true"] {
        .icon {
          transform: rotate(-180deg);
        }
      }

      > span {
        display: block;
        width: 100%;
        padding-right: calc(100% - 17rem);
        z-index: 1;
        @include for-desktop-up {
          padding-right: calc(100% - 37rem);
        }
      }
    }
  }

  &-answer {
    height: 0;
    opacity: 0;
    overflow: hidden;
    transform: translateY(-3rem);
    transition: all ease 0.5s;

    &.answer-active {
      height: auto;
      opacity: 1;
      transform: translateY(0);
    }

    .title {
      display: none;
    }

    .title + .faq-list {
      margin-top: 0;
    }

    .text {
      padding: 1.5rem 0.25rem 1.5rem 1.25rem;
      color: var(--text-70);
      @include for-desktop-up {
        padding: 2.5rem 2.5rem 2.5rem 4.5rem;
      }
    }
    .faq-item {
      border-color: var(--solid-03);
      .text {
        padding-top: 0;
      }
    }
    .btn-faq {
      @include pair-7;
    }
  }
  &.section .title {
    text-align: center;
    max-width: 54rem;
    margin: 0 auto;
  }
}

.title + .faq-list {
  margin-top: 3rem;
  @include for-desktop-up {
    margin-top: 4rem;
  }
}

.background.alternate {
  .faq {
    .btn-faq {
      background: white;

      @include pair-faq-button-alternate;
    }

    .faq-item {
      border-bottom: transparent;
      @include pair-faq-item-alternate;
    }
  }
}
</style>
<style lang="scss">
.collection-tabs-container:not(.with-tabs) {
  .faq-list {
    max-width: none;
  }
  .faq-item .btn-faq {
    padding-left: 2rem;
    @include for-tablet-landscape-up {
      padding-left: 2.5rem;
      padding-right: 2.5rem;
    }
    @include for-desktop-up {
      padding-left: 3.5rem;
      padding-right: 3.5rem;
    }
    .icon {
      right: 1.5rem;
      @include for-tablet-landscape-up {
        right: 2rem;
      }
      @include for-desktop-up {
        right: 3.5rem;
      }
    }
  }
}
</style>
